import { hasFieldAccess } from '@Data/utils/policyManifest';
import { FormFieldLabel } from '@Pages/pageRuntime/components/forms/FieldLabel';
import { LazyFormFieldDisplay } from '@Pages/pageRuntime/components/forms/LazyFieldDisplay';
import { compareFieldSortOrder } from '@Pages/pageRuntime/sdk/fieldOrdering';
import type {
  SdkApiError,
  SdkRecordHandle,
  SdkReferenceRelationPath,
  SdkResource,
  SdkResourceQueryHandle,
  SdkResourceField,
  SdkResourceRecord,
  SdkResourceRelationPath
} from '@Pages/pageRuntime/sdk/types';
import { useMemo, useRef } from 'react';

import { PolicyScope, VariableType, type RecordScope } from '@/types/enums';

import { useRecord } from './useRecord';

const SHOW_SCOPE = Number(PolicyScope.Show);

export type ShowResourceRecordResource = SdkResourceQueryHandle<'show'> &
  Pick<SdkResource, 'fields'>;

type UseShowResourceRecordOptions<
  TResource extends ShowResourceRecordResource,
  TRelations extends readonly SdkResourceRelationPath<NoInfer<TResource>>[]
> = {
  resource: TResource;
  recordId: string | null | undefined;
  /** Explicit field handles controlling which fields are shown and their order.
   * Omit to use all non-system, non-virtual fields accessible at Show scope. */
  fields?: SdkResourceField[];
  /** Additional relation paths to preload alongside relations implied by displayed fields. */
  relations?: TRelations;
  /** Soft-delete subset to fetch from. Defaults to active records only; pass
   * `RecordScope.ActiveOrArchived` to also show archived records (e.g. an admin
   * detail surface that can view or restore deleted records). */
  scope?: RecordScope;
};

export type ShowResourceRecordFieldComponentProps = {
  field: SdkResourceField;
  className?: string;
};

export type ShowResourceRecordFieldValueProps = ShowResourceRecordFieldComponentProps & {
  /**
   * Rendered in place of the default em dash when the field's value is empty.
   * Pass `null` to render nothing at all. Omit to inherit an enclosing fallback
   * (such as `KeyValueList`'s) or keep the em dash. The fallback renders exactly
   * as given — the caller owns its styling.
   */
  fallback?: React.ReactNode;
};

export type SdkRecordView<TRecord extends Record<string, unknown> = Record<string, unknown>> = {
  fields: SdkResourceField[];
  record: TRecord | undefined;
  isLoading: boolean;
  error: SdkApiError | null;
  FieldLabel: React.ComponentType<ShowResourceRecordFieldComponentProps>;
  FieldValue: React.ComponentType<ShowResourceRecordFieldValueProps>;
};

export function useShowResourceRecord<
  TResource extends ShowResourceRecordResource,
  const TRelations extends readonly SdkResourceRelationPath<NoInfer<TResource>>[] = readonly []
>({
  resource,
  recordId,
  fields: overrideFields,
  relations,
  scope
}: UseShowResourceRecordOptions<TResource, TRelations>): SdkRecordView<
  SdkResourceRecord<TResource, TRelations>
> {
  const rawFields = useMemo<SdkResourceField[]>(() => {
    // Treat an explicit (even empty) array as the caller controlling the field set;
    // only fall back to defaults when `fields` is omitted entirely.
    if (overrideFields !== undefined) {
      return overrideFields.filter(Boolean);
    }

    return Object.values(resource.fields)
      .filter((f) => !f.is_system_field && !f.is_virtual_field && hasFieldAccess(f, SHOW_SCOPE))
      .toSorted(compareFieldSortOrder);
  }, [resource.fields, overrideFields]);

  const relationKeys = useMemo(
    () =>
      Array.from(
        new Set([
          ...rawFields.flatMap((f) => (f.type === VariableType.Relation ? [f.key] : [])),
          ...(relations ?? [])
        ])
      ),
    [rawFields, relations]
  );

  const {
    data: record,
    isLoading,
    error
  } = useRecord(resource, recordId, {
    relations: relationKeys as unknown as readonly SdkReferenceRelationPath<TResource>[],
    enabled: Boolean(recordId),
    scope
  }) as SdkRecordHandle<SdkResourceRecord<TResource, TRelations>>;

  // Keep the latest record available to the stable component closures without
  // recreating those components on every record change.
  const recordRef = useRef(record);
  recordRef.current = record;

  // FieldValue is created once per hook invocation so its component identity is
  // stable across renders, preventing unnecessary remounts. The closure captures
  // `recordRef` (the object, not its .current), so it always reads the latest
  // record at render time.
  const FieldValue = useMemo(
    () =>
      function FieldValueComponent({
        field,
        fallback,
        className
      }: ShowResourceRecordFieldValueProps) {
        const rec = recordRef.current;
        return (
          <LazyFormFieldDisplay
            field={field}
            value={rec?.[field.key]}
            fallback={fallback}
            className={className}
          />
        );
      },
    []
  );

  return {
    fields: rawFields,
    record: record as SdkResourceRecord<TResource, TRelations> | undefined,
    isLoading,
    error,
    FieldLabel: FormFieldLabel,
    FieldValue
  };
}
